import { buildMunicipalPropertyCreateFormSchema } from "@/domain/municipal-property/models/build-municipal-property-create-form-schema";
import { toCreateMunicipalPropertyInput } from "@/domain/municipal-property/models/to-create-municipal-property-input";
import { WorkspaceMunicipalityOption } from "@/domain/municipal-property/models/workspace-municipality-option.model";
import { CreateMunicipalPropertyInput } from "@/graphql/generated/graphql";
import { resolveMaxCommissioningYear } from "@ark/shared";

interface ValidatePropertyCsvRowInput {
  row: Record<string, unknown>;
  workspaceId: string;
  municipalityList: readonly WorkspaceMunicipalityOption[];
  typeLabelMap: Record<string, string>;
}

export function validatePropertyCsvRow({
  row,
  workspaceId,
  municipalityList,
  typeLabelMap,
}: ValidatePropertyCsvRowInput):
  | { success: true; data: CreateMunicipalPropertyInput }
  | { success: false; field: string } {
  const text = (key: string): string => String(row[key] ?? "").trim();
  const municipality = text("municipality") === "__unassigned__" ? "" : text("municipality");
  const matchList = municipalityList.filter((entry) => entry.name === municipality || entry.id === municipality);
  if (municipality && matchList.length !== 1) return { success: false, field: "municipality" };
  const type = Object.keys(typeLabelMap).find((key) => key === text("type") || typeLabelMap[key] === text("type"));
  const area = text("usableFloorAreaM2");
  if (area && !/^(?:\d+|\d{1,3}(?:\.\d{3})+)(?:,\d+)?$/.test(area))
    return { success: false, field: "usableFloorAreaM2" };
  const year = text("commissioningYear");
  if (year && !/^\d{4}$/.test(year)) return { success: false, field: "commissioningYear" };
  const effectiveYear = text("effectiveYear") || year;
  if (!/^\d{4}$/.test(effectiveYear)) return { success: false, field: "effectiveYear" };
  const schema = buildMunicipalPropertyCreateFormSchema({
    maxCommissioningYear: resolveMaxCommissioningYear(),
    messages: {
      nameRequired: "invalid",
      nameTooLong: "invalid",
      floorAreaPositive: "invalid",
      commissioningYearRange: "invalid",
      effectiveYearRange: "invalid",
    },
  });
  if (!text("effectiveYear") && !schema.shape.commissioningYear.safeParse(Number(year)).success)
    return { success: false, field: "effectiveYear" };
  const result = schema.safeParse({
    effectiveYear: Number(effectiveYear),
    name: text("name"),
    type,
    municipalityIdList: municipality ? matchList.map((entry) => entry.id) : [],
    ...Object.fromEntries(
      ["description", "street", "houseNumber", "postalCode", "city"].map((key) => [key, text(key)]),
    ),
    usableFloorAreaM2: area ? Number(area.replaceAll(".", "").replace(",", ".")) : null,
    commissioningYear: year ? Number(year) : null,
  });
  return result.success
    ? { success: true, data: toCreateMunicipalPropertyInput({ workspaceId, values: result.data }) }
    : { success: false, field: String(result.error.issues[0].path[0]) };
}
